/**
 * CTRL_07_FairnessDashboard - Shows fairness metrics and bias testing results
 */
import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Progress, Table, Tag, Select, Spin, Alert, Empty } from 'antd';
import {
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  LineChartOutlined
} from '@ant-design/icons';
import { showError } from '../../../utils/notifications';

const { Option } = Select;

function CTRL_07_FairnessDashboard({ projectId }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [timeRange, setTimeRange] = useState('7d');

  useEffect(() => {
    if (projectId) {
      fetchData();
    }
  }, [projectId, timeRange]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Mock data for now - in production, call API endpoint
      // const response = await getFairnessMetrics(projectId, timeRange);
      const mockData = {
        overall_status: 'pass',
        coverage_percentage: 85,
        tests_run: 120,
        tests_passed: 115,
        tests_failed: 5,
        metrics: [
          {
            name: 'Demographic Parity',
            score: 0.15,
            status: 'pass',
            threshold: 0.2,
            description: 'Difference in selection rates: 15%'
          },
          {
            name: 'Disparate Impact',
            score: 0.85,
            status: 'pass',
            threshold: 0.8,
            description: '85% ratio (80% rule compliant)'
          },
          {
            name: 'Equalized Odds',
            score: 0.12,
            status: 'pass',
            threshold: 0.2,
            description: 'TPR/FPR difference: 12%'
          },
          {
            name: 'Equal Opportunity',
            score: 0.10,
            status: 'pass',
            threshold: 0.2,
            description: 'TPR difference: 10%'
          }
        ],
        protected_attributes: [
          {
            attribute: 'gender',
            tests: 30,
            passed: 28,
            failed: 2,
            avg_bias_score: 0.14
          },
          {
            attribute: 'race',
            tests: 30,
            passed: 29,
            failed: 1,
            avg_bias_score: 0.11
          },
          {
            attribute: 'age',
            tests: 30,
            passed: 29,
            failed: 1,
            avg_bias_score: 0.09
          }
        ],
        recent_violations: [
          {
            timestamp: '2025-11-02 14:32:00',
            model: 'credit-approval-v2',
            metric: 'demographic_parity',
            score: 0.25,
            attribute: 'gender'
          },
          {
            timestamp: '2025-11-01 09:15:00',
            model: 'loan-classifier-v3',
            metric: 'disparate_impact',
            score: 0.75,
            attribute: 'race'
          }
        ]
      };
      setData(mockData);
    } catch (error) {
      console.error('Failed to fetch fairness metrics:', error);
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !data) {
    return (
      <Card>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card>
        <Empty description="No fairness data available. Configure CTRL_07 control to start testing." />
      </Card>
    );
  }

  const passRate = data.tests_run > 0 ? ((data.tests_passed / data.tests_run) * 100) : 0;
  const passRateStatus = passRate >= 90 ? 'success' : passRate >= 70 ? 'warning' : 'exception';

  const metricsColumns = [
    {
      title: 'Metric',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Score',
      dataIndex: 'score',
      key: 'score',
      render: (score) => <span>{score.toFixed(3)}</span>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'pass' ? 'green' : 'red'} icon={status === 'pass' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
          {status.toUpperCase()}
        </Tag>
      )
    },
    {
      title: 'Threshold',
      dataIndex: 'threshold',
      key: 'threshold'
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description'
    }
  ];

  const attributesColumns = [
    {
      title: 'Protected Attribute',
      dataIndex: 'attribute',
      key: 'attribute',
      render: (attr) => <Tag color="blue">{attr}</Tag>
    },
    {
      title: 'Tests Run',
      dataIndex: 'tests',
      key: 'tests'
    },
    {
      title: 'Passed',
      dataIndex: 'passed',
      key: 'passed',
      render: (val) => <span style={{ color: '#52c41a' }}>{val}</span>
    },
    {
      title: 'Failed',
      dataIndex: 'failed',
      key: 'failed',
      render: (val) => <span style={{ color: val > 0 ? '#f5222d' : '#52c41a' }}>{val}</span>
    },
    {
      title: 'Avg Bias Score',
      dataIndex: 'avg_bias_score',
      key: 'avg_bias_score',
      render: (score) => (
        <span style={{ color: score > 0.2 ? '#f5222d' : '#52c41a' }}>
          {score.toFixed(3)}
        </span>
      )
    }
  ];

  const violationsColumns = [
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp'
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
      render: (text) => <Tag>{text}</Tag>
    },
    {
      title: 'Metric',
      dataIndex: 'metric',
      key: 'metric'
    },
    {
      title: 'Score',
      dataIndex: 'score',
      key: 'score',
      render: (score) => <span style={{ color: '#f5222d' }}>{score.toFixed(3)}</span>
    },
    {
      title: 'Attribute',
      dataIndex: 'attribute',
      key: 'attribute',
      render: (attr) => <Tag color="orange">{attr}</Tag>
    }
  ];

  return (
    <div>
      {/* Header Controls */}
      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={16} align="middle">
          <Col span={4}>
            <strong>Time Range:</strong>
          </Col>
          <Col span={8}>
            <Select
              value={timeRange}
              onChange={setTimeRange}
              style={{ width: '100%' }}
            >
              <Option value="7d">Last 7 Days</Option>
              <Option value="30d">Last 30 Days</Option>
              <Option value="90d">Last 90 Days</Option>
            </Select>
          </Col>
        </Row>
      </Card>

      {/* Overall Status */}
      <Alert
        type={data.overall_status === 'pass' ? 'success' : 'error'}
        message={data.overall_status === 'pass' ? 'All Fairness Tests Passing' : 'Fairness Violations Detected'}
        description={`${data.tests_run} tests run, ${data.tests_passed} passed, ${data.tests_failed} failed`}
        showIcon
        icon={data.overall_status === 'pass' ? <CheckCircleOutlined /> : <WarningOutlined />}
        style={{ marginBottom: '16px' }}
      />

      {/* Key Metrics */}
      <Row gutter={16} style={{ marginBottom: '16px' }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Test Pass Rate"
              value={passRate}
              precision={1}
              suffix="%"
              valueStyle={{ color: passRate >= 90 ? '#3f8600' : passRate >= 70 ? '#faad14' : '#cf1322' }}
              prefix={passRate >= 90 ? <CheckCircleOutlined /> : <WarningOutlined />}
            />
            <Progress percent={passRate} status={passRateStatus} showInfo={false} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Tests Run"
              value={data.tests_run}
              prefix={<LineChartOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Tests Passed"
              value={data.tests_passed}
              valueStyle={{ color: '#3f8600' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Tests Failed"
              value={data.tests_failed}
              valueStyle={{ color: data.tests_failed > 0 ? '#cf1322' : '#3f8600' }}
              prefix={data.tests_failed > 0 ? <CloseCircleOutlined /> : <CheckCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Fairness Metrics Table */}
      <Card title="Fairness Metrics" style={{ marginBottom: '16px' }}>
        <Table
          dataSource={data.metrics}
          columns={metricsColumns}
          pagination={false}
          rowKey="name"
          size="small"
        />
      </Card>

      {/* Protected Attributes Breakdown */}
      <Card title="Protected Attributes Breakdown" style={{ marginBottom: '16px' }}>
        <Table
          dataSource={data.protected_attributes}
          columns={attributesColumns}
          pagination={false}
          rowKey="attribute"
          size="small"
        />
      </Card>

      {/* Recent Violations */}
      {data.recent_violations && data.recent_violations.length > 0 && (
        <Card title="Recent Fairness Violations" style={{ marginBottom: '16px' }}>
          <Table
            dataSource={data.recent_violations}
            columns={violationsColumns}
            pagination={false}
            rowKey={(record, idx) => idx}
            size="small"
          />
        </Card>
      )}
    </div>
  );
}

export default CTRL_07_FairnessDashboard;
